import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, IndianRupee, RefreshCcw, CircleDollarSign } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import RazorpayCheckoutButton from "@/components/RazorpayCheckoutButton";
import RazorpaySubscribeButton from "@/components/RazorpaySubscribeButton";

const USD_TO_INR = 83;

const FALLBACK_TIERS = [
  {
    slug: "starter", name: "Starter", monthly: 29, yearly: 290,
    tagline: "For solo affiliates testing the waters",
    features: ["10 AI-generated articles per month", "1 affiliate network connection", "Basic analytics", "Refresh Radar (weekly)"],
    highlight: false,
  },
  {
    slug: "professional", name: "Professional", monthly: 79, yearly: 790,
    tagline: "Most popular — for serious creators", highlight: true,
    features: ["Unlimited AI-generated articles", "8 affiliate networks", "Multilingual publishing (8 langs)", "Live PA-API product data", "Auto-tweet + digest emails", "Priority AI (GPT-5.6 Terra + Nano Banana)"],
  },
  {
    slug: "business", name: "Business", monthly: 199, yearly: 1990,
    tagline: "For teams and larger portfolios",
    features: ["Everything in Professional", "10 team seats", "Custom domains", "Advanced analytics", "24/7 priority support"],
    highlight: false,
  },
];

export default function PricingPage() {
  const [cycle, setCycle] = useState("monthly");
  const [mode, setMode] = useState("recurring"); // "recurring" (auto-renew) | "one_time"
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const { user } = useAuth();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/pricing-plans");
        if (Array.isArray(data.plans) && data.plans.length > 0) setTiers(data.plans);
      } catch { /* keep fallback */ }
    })();
  }, []);
  return (
    <div className="max-w-[1400px] mx-auto px-6 py-20 fade-up">
      <div className="text-center max-w-3xl mx-auto">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">— Pricing</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-sky-100 tracking-tight">
          Simple, honest, <span className="italic text-sky-400">no gotchas.</span>
        </h1>
        <p className="mt-4 text-neutral-400 leading-relaxed">
          Try any tier free for the first 3 articles. Cancel or switch anytime. Yearly saves you two months.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <div className="inline-flex border border-slate-800 rounded-full p-1">
          {["monthly", "yearly"].map((c) => (
            <button
              key={c}
              data-testid={`cycle-${c}`}
              onClick={() => setCycle(c)}
              className={`px-5 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition ${
                cycle === c ? "bg-sky-400 text-slate-950" : "text-neutral-400"
              }`}
            >
              {c}{c === "yearly" && " · save 17%"}
            </button>
          ))}
        </div>

        <div className="inline-flex border border-slate-800 rounded-full p-1">
          <button
            data-testid="mode-recurring"
            onClick={() => setMode("recurring")}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition flex items-center gap-1.5 ${
              mode === "recurring" ? "bg-sky-400 text-slate-950" : "text-neutral-400"
            }`}
          >
            <RefreshCcw className="w-3 h-3" /> auto-renew
          </button>
          <button
            data-testid="mode-one-time"
            onClick={() => setMode("one_time")}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition flex items-center gap-1.5 ${
              mode === "one_time" ? "bg-sky-400 text-slate-950" : "text-neutral-400"
            }`}
          >
            <CircleDollarSign className="w-3 h-3" /> one-time
          </button>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map((t) => (
          <div
            key={t.slug || t.name}
            data-testid={`plan-${(t.slug || t.name).toLowerCase()}`}
            className={`border rounded-2xl p-8 relative ${
              t.highlight
                ? "border-sky-400 bg-gradient-to-br from-sky-500/10 to-transparent scale-[1.02]"
                : "border-slate-800"
            }`}
          >
            {t.highlight && (
              <span className="absolute -top-3 left-8 bg-sky-400 text-slate-950 text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">Most popular</span>
            )}
            <h3 className="font-serif text-2xl text-sky-100">{t.name}</h3>
            <p className="text-xs text-neutral-500 mt-1">{t.tagline}</p>
            <div className="mt-6">
              <span className="font-serif text-5xl font-numeric text-sky-100">${t[cycle]}</span>
              <span className="text-sm text-neutral-500 ml-2">/{cycle === "monthly" ? "mo" : "yr"}</span>
              <p className="text-[11px] font-mono text-sky-400/70 mt-1 flex items-center gap-1">
                <IndianRupee className="w-3 h-3" />
                {(t[cycle] * USD_TO_INR).toLocaleString("en-IN")} · billed in INR via Razorpay
              </p>
            </div>
            {user ? (
              <div className="mt-6">
                {mode === "recurring" ? (
                  <RazorpaySubscribeButton
                    planSlug={t.slug || t.name.toLowerCase()}
                    billingCycle={cycle}
                    highlight={t.highlight}
                    label={`Subscribe · auto-renew ${cycle}`}
                    testid={`subscribe-${(t.slug || t.name).toLowerCase()}-${cycle}`}
                  />
                ) : (
                  <RazorpayCheckoutButton
                    planSlug={t.slug || t.name.toLowerCase()}
                    billingCycle={cycle}
                    highlight={t.highlight}
                    label={`Get ${t.name} · one-time`}
                    testid={`plan-cta-${(t.slug || t.name).toLowerCase()}`}
                  />
                )}
              </div>
            ) : (
              <Link
                to="/signup"
                data-testid={`plan-cta-${(t.slug || t.name).toLowerCase()}`}
                className={`mt-6 block text-center py-3 rounded-md font-medium transition ${
                  t.highlight
                    ? "bg-sky-400 hover:bg-sky-300 text-slate-950"
                    : "border border-sky-400 text-sky-100 hover:bg-sky-400 hover:text-slate-950"
                }`}
              >
                Sign up to subscribe
              </Link>
            )}
            <ul className="mt-6 space-y-2.5 text-sm">
              {(t.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2 text-neutral-300">
                  <Check className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-16 text-center">
        <p className="text-sm text-neutral-500">Want to see it before you sign up? <Link to="/browse" className="text-sky-300 border-b border-sky-400/40">Browse articles →</Link></p>
      </div>
    </div>
  );
}
